export const PLAN_CODE_ORDER = [
  'free',
  'credit_pack',
  'solo',
  'business',
  'growth',
  'enterprise',
  'launch',
  'flow',
  'boost',
  'infinity',
];

export function isCreditPackPlan(plan) {
  if (!plan || typeof plan !== 'object') return false;
  const kind = String(plan?.planKind ?? plan?.plan_kind ?? '').toLowerCase();
  if (kind === 'credit_pack') return true;

  const code = String(plan?.code ?? plan?.slug ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (code === 'credit_pack' || code.includes('credit_pack')) return true;

  const name = String(plan?.name ?? '').trim().toLowerCase();
  if (name.includes('credit pack')) return true;

  if (Array.isArray(plan?.creditPacks) && plan.creditPacks.length > 0) return true;
  if (Array.isArray(plan?.credit_packs) && plan.credit_packs.length > 0) return true;
  if (Array.isArray(plan?.raw?.credit_packs) && plan.raw.credit_packs.length > 0) return true;

  return false;
}

/** Split catalog into recurring subscription cards vs pay-as-you-go credit packs. */
export function partitionCatalogPlans(apiPlans) {
  const subscription = [];
  const creditPacks = [];
  (Array.isArray(apiPlans) ? apiPlans : []).forEach((plan) => {
    if (isCreditPackPlan(plan)) creditPacks.push(plan);
    else subscription.push(plan);
  });
  return { subscription, creditPacks };
}

export function normalizeBillingInterval(value) {
  const raw = String(value || 'month').toLowerCase();
  if (raw === 'year' || raw === 'yearly' || raw === 'annual' || raw === 'annually') {
    return 'year';
  }
  return 'month';
}

export function planCodeKey(plan) {
  const raw = String(plan?.code ?? plan?.slug ?? plan?.id ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return '';
  return raw.replace(/[-_]?(monthly|yearly|annual|year|month)$/i, '');
}

function toNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Monthly billed amount from API `pricing.monthly`. */
export function extractMonthlyPrice(plan) {
  if (!plan) return null;
  if (plan.priceMonthly != null) return toNumber(plan.priceMonthly);

  const raw = plan.raw?.pricing;
  if (raw && typeof raw === 'object' && raw.monthly != null) {
    return toNumber(raw.monthly);
  }
  return null;
}

/** Annual total from API `pricing.annual` (e.g. 84/year, not per-month). */
export function extractYearlyPrice(plan) {
  if (!plan) return null;
  if (plan.priceYearly != null) return toNumber(plan.priceYearly);

  const raw = plan.raw?.pricing;
  if (raw && typeof raw === 'object' && raw.annual != null) {
    return toNumber(raw.annual);
  }
  return null;
}

function extractAnnualSavings(plan) {
  if (!plan) return null;
  if (plan.annualSavings != null) return toNumber(plan.annualSavings);
  const raw = plan.raw?.pricing;
  if (raw?.annual_savings != null) return toNumber(raw.annual_savings);
  return null;
}

function mergeGroupMeta(entry, plan) {
  if (plan.name) entry.name = plan.name;
  if (plan.tagline) entry.tagline = plan.tagline;
  if (plan.description) entry.description = plan.description;
  if (plan.features?.length) entry.features = plan.features;
  if (plan.envelopeLimitLabel) entry.envelopeLimitLabel = plan.envelopeLimitLabel;
  if (plan.envelopesPerMonth != null) entry.envelopesPerMonth = plan.envelopesPerMonth;
  if (plan.usersAllowed != null) entry.usersAllowed = plan.usersAllowed;
  if (plan.currency) entry.currency = plan.currency;
  if (plan.isCurrent) entry.isCurrent = true;
  if (plan.isRecommended) entry.isRecommended = true;
  if (plan.isContactSales) entry.isContactSales = true;
  if (plan.id != null) entry.apiId = plan.id;
  if (plan.code) entry.apiPlanCode = plan.code;

  const savings = extractAnnualSavings(plan);
  if (savings != null) entry.annualSavings = savings;
}

function buildPriceVariant(plan, interval, price) {
  const isYear = interval === 'year';
  const isContact = price == null;

  return {
    id: plan.id,
    code: planCodeKey(plan),
    apiPlanCode: plan.code,
    name: plan.name,
    tagline: plan.tagline,
    description: plan.description,
    interval,
    price: isContact ? null : price,
    isContactSales: isContact,
    currency: plan.currency || 'USD',
    features: plan.features,
    envelopesPerMonth: plan.envelopesPerMonth,
    envelopeLimitLabel: plan.envelopeLimitLabel,
    stripePriceId: isYear
      ? plan.stripePriceIdYearly ?? plan.stripePriceId
      : plan.stripePriceId,
  };
}

/**
 * One card per plan; same features/metadata on both tabs — only price differs.
 * Skips `plan_kind: credit_pack` — those are rendered separately.
 */
export function groupPlansByCode(apiPlans) {
  const map = new Map();

  (Array.isArray(apiPlans) ? apiPlans : []).forEach((plan) => {
    if (isCreditPackPlan(plan)) return;
    const code = planCodeKey(plan);
    if (!code) return;

    if (!map.has(code)) {
      map.set(code, {
        code,
        name: plan.name,
        tagline: plan.tagline,
        description: plan.description,
        features: plan.features ?? [],
        envelopeLimitLabel: plan.envelopeLimitLabel,
        envelopesPerMonth: plan.envelopesPerMonth,
        usersAllowed: plan.usersAllowed,
        currency: (plan.currency || 'USD').toUpperCase(),
        monthlyPrice: null,
        yearlyPrice: null,
        annualSavings: null,
        monthly: null,
        yearly: null,
        isCurrent: false,
        isRecommended: false,
        isContactSales: false,
        apiId: null,
        apiPlanCode: plan.code,
      });
    }

    const entry = map.get(code);
    mergeGroupMeta(entry, plan);

    const monthlyPrice = extractMonthlyPrice(plan);
    const yearlyPrice = extractYearlyPrice(plan);

    if (monthlyPrice != null || plan.isContactSales) {
      entry.monthlyPrice = monthlyPrice;
      entry.monthly = buildPriceVariant(plan, 'month', monthlyPrice);
    }
    if (yearlyPrice != null || plan.isContactSales) {
      entry.yearlyPrice = yearlyPrice;
      entry.yearly = buildPriceVariant(plan, 'year', yearlyPrice);
    }
  });

  return Array.from(map.values());
}

export function buildDisplayPlanGroups(apiPlans) {
  return groupPlansByCode(apiPlans);
}

export function sortPlanGroups(groups) {
  return [...groups].sort((a, b) => {
    const ai = PLAN_CODE_ORDER.indexOf(a.code);
    const bi = PLAN_CODE_ORDER.indexOf(b.code);
    const aOrder = ai === -1 ? 999 : ai;
    const bOrder = bi === -1 ? 999 : bi;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a.name).localeCompare(String(b.name));
  });
}

export function getPlanVariant(group, billingInterval) {
  if (!group) return null;
  const interval = normalizeBillingInterval(billingInterval);
  if (interval === 'year') return group.yearly ?? null;
  return group.monthly ?? null;
}

export function filterPlanGroupsForInterval(groups, billingInterval) {
  const interval = normalizeBillingInterval(billingInterval);
  return (Array.isArray(groups) ? groups : []).filter((group) => {
    if (group.isContactSales) return true;
    return interval === 'year'
      ? group.yearlyPrice != null || group.yearly?.isContactSales
      : group.monthlyPrice != null || group.monthly?.isContactSales;
  });
}

export function formatEnvelopeLabel(group) {
  if (group?.envelopeLimitLabel) return group.envelopeLimitLabel;
  return formatEnvelopeAllowance(group?.envelopesPerMonth);
}

export function formatEnvelopeAllowance(limit) {
  if (limit == null || limit === 0) return 'Unlimited envelopes per month';
  return `${limit} envelope${limit === 1 ? '' : 's'} per month`;
}

export function formatPriceParts(plan, billingInterval) {
  if (!plan) {
    return { amount: null, suffix: '', display: 'Contact us', full: 'Contact us', isFree: false, isContact: true };
  }
  if (plan.isContactSales) {
    return {
      amount: null,
      suffix: '',
      display: 'Contact us',
      full: 'Contact us',
      isFree: false,
      isContact: true,
    };
  }
  if (plan.price === 0) {
    const interval = normalizeBillingInterval(plan.interval ?? billingInterval);
    const suffix = interval === 'year' ? '/year' : '/month';
    return {
      amount: '$0',
      suffix,
      display: '$0',
      full: `$0${suffix}`,
      isFree: true,
      isContact: false,
    };
  }
  const currency = plan.currency || 'USD';
  const interval = normalizeBillingInterval(plan.interval ?? billingInterval);
  const suffix = interval === 'year' ? '/year' : '/month';
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: plan.price % 1 === 0 ? 0 : 2,
    }).format(plan.price);
    return {
      amount: formatted,
      suffix,
      display: formatted,
      full: `${formatted}${suffix}`,
      isFree: false,
      isContact: false,
    };
  } catch {
    const amount = `$${plan.price}`;
    return {
      amount,
      suffix,
      display: amount,
      full: `${amount}${suffix}`,
      isFree: false,
      isContact: false,
    };
  }
}

export function computeAnnualSavingsLabel(group) {
  if (group?.annualSavings != null && group.annualSavings > 0) {
    try {
      const label = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: group.currency || 'USD',
        maximumFractionDigits: 0,
      }).format(group.annualSavings);
      return `Save ${label} annually`;
    } catch {
      return `Save $${Math.round(group.annualSavings)} annually`;
    }
  }

  const monthly = group?.monthlyPrice;
  const yearly = group?.yearlyPrice;
  if (
    monthly == null
    || yearly == null
    || monthly === 0
  ) {
    return null;
  }
  const saved = monthly * 12 - yearly;
  if (saved <= 0) return null;
  try {
    const label = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: group.currency || 'USD',
      maximumFractionDigits: 0,
    }).format(saved);
    return `Save ${label} annually`;
  } catch {
    return `Save $${Math.round(saved)} annually`;
  }
}

export function formatCreditPackPrice(pack, currency = 'USD') {
  const price = Number(pack?.price);
  if (!Number.isFinite(price)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);
  } catch {
    return `$${price.toFixed(2)}`;
  }
}

export function findPlanGroup(groups, planCode) {
  const key = String(planCode || '').trim().toLowerCase();
  return groups.find((g) => g.code === key) ?? null;
}

export const PLAN_MARKETING = {};
